/**
 * «Экспорт в PDF»: a conversation or an investigation as an A4 report with the evidence its answers cite.
 *
 * buildConversationPdf() is the generator: jsPDF, jspdf-autotable and the link resolver come from the caller,
 * so it runs outside the browser too. exportConversationPdf() loads the libraries and the Cyrillic font on
 * demand (the chat bundle does not carry them), loads the missing evidence and saves the file.
 */
import dayjs from 'dayjs';
import { marked } from 'marked';
import { count, fileSize, formatTime } from './format';
import { recordLink, reportLink, streamLink } from './links';

const FONT = 'Roboto-Regular';
const PAGE_HEIGHT = 297;
const LEFT = 15;
const WIDTH = 180; // A4 width without the 15 mm margins
const TOP = 20; // content starts below the running header
const LIMIT = 279; // and ends above the footer
const PT = 25.4 / 72; // millimetres in a point
const BATCH = 100; // evidence ids per request
const REPORT_ROWS = 50;
const CARD_INDENT = 8;

const COLORS = {
  text: [11, 49, 95],
  body: [33, 45, 61],
  muted: [102, 124, 153],
  primary: [20, 120, 232],
  border: [215, 227, 241],
  soft: [245, 248, 252],
  primarySoft: [234, 243, 255],
  code: [241, 244, 248],
  danger: [204, 83, 101],
  warning: [196, 132, 22],
};
const LEVEL_COLORS = { high: COLORS.danger, medium: COLORS.warning };

const EVIDENCE = /E-[0-9a-f]{12,24}/g;
// Citations as markdown.js finds them: "[E-…]", "[E-…, E-…]" or a bare id.
const CITATION = /\[\s*E-[0-9a-f]{12,24}(?:\s*[,;]\s*E-[0-9a-f]{12,24})*\s*\]|(^|[^\w-])(E-[0-9a-f]{12,24})(?![\w-])/g;
// Numbered citations of the PDF text: "[3]" or "[3, 4]" (a no-break space keeps a group on one line).
const NUMBERED = /\[(\d+(?:,\s\d+)*)\]/g;
const WEB_ADDRESS = /https?:\/\/[^\s<>«»"]+/g;

// Characters the embedded Roboto subset has no glyphs for (other symbols and emoji are dropped).
const REPLACEMENTS = {
  '→': '->',
  '←': '<-',
  '↔': '<->',
  '⇒': '=>',
  '✓': '+',
  '✔': '+',
  '✗': 'x',
  '✘': 'x',
  '₽': 'руб.',
  '\t': '    ',
  '‑': '-',
  '−': '-',
  ' ': ' ',
};

const PROBLEMS = {
  failed: 'ответ не получен из-за ошибки',
  interrupted: 'работа была прервана',
  cancelled: 'остановлено пользователем',
};
// What the text of a file read by the assistant is (as EvidenceCard.vue).
const TEXT_LABELS = { description: 'Что на изображении', transcript: 'Речь', text: 'Текст' };

/** Conversation timestamps (UTC ISO) in the browser time zone. */
function when(value) {
  if (!value) return '';
  const moment = dayjs(value);
  return moment.isValid() ? moment.format('DD.MM.YYYY HH:mm') : '';
}

function decode(text) {
  const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
  return String(text || '').replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, name) => {
    if (name[0] !== '#') return named[name.toLowerCase()] || entity;
    const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : entity;
  });
}

/** Plain text of inline markdown tokens: emphasis dropped, a link as "text (address)". */
function inline(tokens, hrefs) {
  return (tokens || [])
    .map((token) => {
      switch (token.type) {
        case 'br':
          return '\n';
        case 'codespan':
        case 'escape':
          return decode(token.text);
        case 'image':
          return decode(token.text);
        case 'html':
          return decode(String(token.text || '').replace(/<[^>]*>/g, ''));
        case 'link': {
          const label = inline(token.tokens, hrefs);
          const href = String(token.href || '');
          if (!href || href[0] === '#') return label;
          const shown = href.replace(/^mailto:/, '');
          if (hrefs) hrefs.push(href);
          return label && label !== href && label !== shown ? `${label} (${shown})` : shown;
        }
        default:
          return token.tokens ? inline(token.tokens, hrefs) : decode(token.text);
      }
    })
    .join('');
}

/** Citations of the answer as numbers of the chat: "[E-…, E-…]" -> "[1, 2]" (an unknown id is "•"). */
export function numberCitations(text, numbers = {}) {
  const label = (ids) => `[${ids.map((id) => numbers[id] || '•').join(', ')}]`;
  return String(text || '').replace(CITATION, (group, prefix, bare) =>
    bare ? `${prefix}${label([bare])}` : label(group.match(EVIDENCE) || []),
  );
}

const trimAddress = (value) => value.replace(/[).,;:!?]+$/, '');

/**
 * Highlighted parts of a line: numbered citations (with the position of each number) and web addresses.
 * A web address broken over lines is linked by its first part to the full address of the paragraph.
 */
function spansOf(text, cite, hrefs) {
  const spans = [];
  if (cite) {
    text.replace(NUMBERED, (match, inner, offset) => {
      const numbers = [];
      inner.replace(/\d+/g, (digits, position) => {
        const start = offset + 1 + position;
        numbers.push({ start, end: start + digits.length, number: Number(digits) });
        return digits;
      });
      if (numbers.length === 1) Object.assign(numbers[0], { start: offset, end: offset + match.length });
      if (numbers.some((entry) => cite.ids[entry.number])) {
        spans.push({ start: offset, end: offset + match.length, numbers });
      }
      return match;
    });
  }
  text.replace(WEB_ADDRESS, (match, offset) => {
    const shown = trimAddress(match);
    const end = offset + shown.length;
    if (shown.length > 10 && !spans.some((span) => span.start < end && offset < span.end)) {
      const url = (hrefs || []).find((href) => href.startsWith(shown)) || shown;
      spans.push({ start: offset, end, url });
    }
    return match;
  });
  return spans.sort((a, b) => a.start - b.start);
}

function problemOf(meta) {
  if (PROBLEMS[meta.status]) return PROBLEMS[meta.status];
  if (meta.partial) return meta.reason === 'length' ? 'достигнут лимит длины ответа' : 'ответ неполный';
  return '';
}

function flagOf(card) {
  if (!card.flag) return null;
  return card.flag >= 2
    ? { text: 'Нарушение политики', color: COLORS.danger }
    : { text: 'Внимание', color: COLORS.warning };
}

function partiesOf(card) {
  const people = [card.actor, card.counterpart].filter(Boolean).join(' — ');
  const direction = { in: 'входящее', out: 'исходящее' }[card.direction] || '';
  return [card.channel, people, direction].filter(Boolean).join(' · ');
}

// Side work findings, as FindingCard.vue shows them.
function findingOf(item) {
  const card = item.card || {};
  const field = (card.fields || []).find((entry) => entry.label === 'Время работы');
  return {
    ...(card.finding || {}),
    file: card.title,
    computer: item.computerName || card.actor,
    activeTime: field ? field.value : null,
  };
}

function clock(value) {
  const text = String(value || '');
  return /\d{2}:\d{2}/.test(text) ? text.slice(11, 16) : '';
}

function minutes(seconds) {
  const total = Math.max(1, Math.round(seconds / 60));
  return total < 60 ? `${total} мин` : `${Math.floor(total / 60)} ч ${total % 60} мин`;
}

function findingPeriod({ first, last }) {
  if (!first) return '';
  if (!last || last === first) return formatTime(first);
  if (String(first).slice(0, 10) === String(last).slice(0, 10)) return `${formatTime(first)}–${clock(last)}`;
  return `${formatTime(first)} – ${formatTime(last)}`;
}

function projectLabel({ project, projectBy }) {
  if (!project) return 'проект не определён';
  const name = [project.code, project.name].filter(Boolean).join(' · ');
  const closed = project.status === 'closed' ? ' (закрыт)' : '';
  return `${name}${closed}${projectBy ? ` — ${projectBy}` : ''}`;
}

/** Links of an evidence card to the web interface (absolute addresses); files of the user have none. */
export function evidenceLinks(item, resolveUrl) {
  const card = (item && item.card) || {};
  if (!item || card.source === 'upload') return [];
  const result = [];
  const add = (label, location) => {
    const url = location ? resolveUrl(location) : null;
    if (url) result.push({ label, url });
  };
  add('Открыть в журнале', recordLink(item));
  add('Лента событий', streamLink(item));
  if (card.kind === 'report') add('Открыть отчёт', reportLink(item));
  return result;
}

/** "<title> <YYYY-MM-DD>.pdf" without the characters Windows does not allow in file names. */
export function fileNameOf(title, date = new Date()) {
  const cleaned = Array.from(String(title || ''))
    .map((ch) => (ch.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(ch) ? '_' : ch))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100)
    .replace(/[. ]+$/, '');
  return `${cleaned || 'Без названия'} ${dayjs(date).format('YYYY-MM-DD')}.pdf`;
}

/** Evidence ids of all answers, each once. numbers: message id -> { evidence id -> number }. */
export function evidenceIdsOf(numbers) {
  const ids = Object.keys(numbers || {}).reduce((result, key) => result.concat(Object.keys(numbers[key] || {})), []);
  return [...new Set(ids)];
}

/**
 * Evidence items by id: the cached ones, the rest requested in batches. An item the server does not return
 * (no access, deleted) stays missing; a failed request marks its items with `error`.
 */
export async function loadEvidence(ids, cached = {}, fetchItems = null) {
  const result = {};
  const missing = [];
  ids.forEach((id) => {
    const item = cached && cached[id];
    if (item && (item.card || item.unavailable)) result[id] = item;
    else missing.push(id);
  });
  if (!missing.length || !fetchItems) return result;
  const batches = [];
  for (let index = 0; index < missing.length; index += BATCH) batches.push(missing.slice(index, index + BATCH));
  const lists = await Promise.all(
    batches.map((batch) =>
      Promise.resolve()
        .then(() => fetchItems(batch))
        .catch(() => batch.map((id) => ({ id, error: true }))),
    ),
  );
  lists.forEach((items) =>
    (items || []).forEach((item) => {
      if (item && item.id) result[item.id] = item;
    }),
  );
  return result;
}

/** Exchanges in order and, for every answer with evidence, its group of the appendix. */
function planOf(messages, numbers) {
  const items = [];
  const groups = [];
  let index = 0;
  let question = '';
  messages.forEach((message) => {
    const meta = message.meta || {};
    if (message.role === 'user') {
      index += 1;
      question = message.content || '';
      items.push({ type: 'question', message, index });
      return;
    }
    // A running answer is not exported.
    if (meta.status === 'running') return;
    const map = numbers[message.id] || {};
    const ids = Object.keys(map).sort((a, b) => map[a] - map[b]);
    const byNumber = ids.reduce((result, id) => ({ ...result, [map[id]]: id }), {});
    const cite = { numbers: map, ids: byNumber, hrefs: [] };
    const answer = { type: 'answer', message, index: Math.max(index, 1), ids, cite };
    items.push(answer);
    if (ids.length) groups.push({ id: message.id, index: answer.index, numbers: map, ids, question });
  });
  return { items, groups };
}

class PdfWriter {
  constructor(doc, autoTable, { resolveUrl, sources, known }) {
    this.doc = doc;
    this.autoTable = autoTable;
    this.resolveUrl = resolveUrl;
    this.sources = sources;
    // Evidence drawn in the appendix: the only targets of internal links.
    this.known = known;
    const font = doc.getFont();
    this.glyphs = font && font.metadata && font.metadata.cmap ? font.metadata.cmap.unicode.codeMap : null;
    this.y = TOP;
    // Internal links are added when the appendix is drawn: { page, x, y, w, h, target }.
    this.pending = [];
    this.targets = {};
  }

  get page() {
    return this.doc.getCurrentPageInfo().pageNumber;
  }

  newPage() {
    this.doc.addPage();
    this.y = TOP;
  }

  ensure(height) {
    if (this.y + height > LIMIT) this.newPage();
  }

  gap(height) {
    if (this.y > TOP) this.y += height;
  }

  style(size, color, font = FONT) {
    this.doc.setFont(font, 'normal');
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
  }

  /** Text the embedded font can draw: unknown symbols are replaced or dropped. */
  safe(value) {
    const text = value === null || value === undefined ? '' : String(value);
    const { glyphs } = this;
    if (!glyphs) return text;
    return Array.from(text)
      .map((ch) => {
        const code = ch.codePointAt(0);
        if (ch === '\n' || glyphs[code]) return ch;
        if (REPLACEMENTS[ch] !== undefined) return REPLACEMENTS[ch];
        if (code < 32 || (code >= 0x2000 && code < 0x2c00) || code >= 0xe000) return '';
        return '?';
      })
      .join('');
  }

  wrap(value, width) {
    return this.safe(value)
      .split('\n')
      .reduce((lines, part) => lines.concat(part ? this.doc.splitTextToSize(part, width) : ['']), []);
  }

  /** One line of the current font cut to the width with "…". */
  fit(value, width) {
    const { doc } = this;
    const text = this.safe(value).replace(/\s+/g, ' ').trim();
    if (doc.getTextWidth(text) <= width) return text;
    let low = 0;
    let high = text.length;
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if (doc.getTextWidth(`${text.slice(0, middle)}…`) <= width) low = middle;
      else high = middle - 1;
    }
    return `${text.slice(0, low).trim()}…`;
  }

  rule(indent = 0) {
    const { doc } = this;
    this.ensure(1);
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.25);
    doc.line(LEFT + indent, this.y, LEFT + WIDTH, this.y);
  }

  strip(color, height, indent = 0) {
    this.ensure(height);
    this.doc.setFillColor(...color);
    this.doc.rect(LEFT + indent, this.y, WIDTH - indent, height, 'F');
    this.y += height;
  }

  /**
   * Wrapped text from the current position, each line checked against the end of the page.
   * fill: background of the lines; bar: x of a quote bar; marker: {text, x, color} on the first line;
   * cite: numbered citations become links to the appendix; target: an internal link of every line.
   */
  text(value, options = {}) {
    const { doc } = this;
    const { size = 10, color = COLORS.body, indent = 0, leading = 1.42, fill = null, bar = null } = options;
    const { marker = null, cite = null, target = null, after = 0, single = false } = options;
    const x = LEFT + indent + (fill ? 2.5 : 0);
    const width = WIDTH - indent - (fill ? 5 : 0);
    this.style(size, color);
    const lines = single ? [this.fit(value, width)] : this.wrap(value, width);
    const hrefs = (cite && cite.hrefs ? cite.hrefs : []).concat(
      (this.safe(value).match(WEB_ADDRESS) || []).map(trimAddress),
    );
    const em = size * PT;
    const height = em * leading;
    lines.forEach((line, index) => {
      this.ensure(height);
      const top = this.y;
      const baseline = top + em * ((leading - 1.17) / 2 + 0.93);
      if (fill) {
        doc.setFillColor(...fill);
        doc.rect(LEFT + indent, top, WIDTH - indent, height, 'F');
      }
      if (bar !== null) {
        doc.setFillColor(...COLORS.border);
        doc.rect(bar, top, 0.8, height, 'F');
      }
      if (marker && index === 0) {
        this.style(size, marker.color || color);
        doc.text(this.safe(marker.text), marker.x, baseline);
        this.style(size, color);
      }
      this.line(line, x, baseline, em, color, cite, hrefs);
      if (target && line) {
        this.pending.push({ page: this.page, x, y: top, w: doc.getTextWidth(line), h: height, target });
      }
      this.y += height;
    });
    this.y += after;
  }

  /** One line: numbered citations and web addresses are coloured and become links. */
  line(text, x, baseline, em, color, cite, hrefs) {
    const { doc } = this;
    const spans = text ? spansOf(text, cite, hrefs) : [];
    if (!spans.length) {
      if (text) doc.text(text, x, baseline);
      return;
    }
    const at = (index) => x + doc.getTextWidth(text.slice(0, index));
    let cursor = 0;
    spans.forEach((span) => {
      if (span.start > cursor) {
        doc.setTextColor(...color);
        doc.text(text.slice(cursor, span.start), at(cursor), baseline);
      }
      doc.setTextColor(...COLORS.primary);
      doc.text(text.slice(span.start, span.end), at(span.start), baseline);
      cursor = span.end;
    });
    doc.setTextColor(...color);
    if (cursor < text.length) doc.text(text.slice(cursor), at(cursor), baseline);
    this.links(text, x, baseline, em, spans, cite);
  }

  links(text, x, baseline, em, spans, cite) {
    const { doc } = this;
    const at = (index) => x + doc.getTextWidth(text.slice(0, index));
    const top = baseline - em * 0.82;
    spans.forEach((span) => {
      if (span.url) {
        doc.link(at(span.start), top, at(span.end) - at(span.start), em * 1.05, { url: span.url });
        return;
      }
      span.numbers.forEach(({ start, end, number }) => {
        const target = cite.ids[number];
        if (!target) return;
        this.pending.push({ page: this.page, x: at(start), y: top, w: at(end) - at(start), h: em * 1.05, target });
      });
    });
  }

  /** Small caps-like label of a section with an optional note on the right. */
  label(text, right = '', color = COLORS.primary) {
    const { doc } = this;
    this.ensure(5.5);
    const baseline = this.y + 3.8;
    this.style(8, color);
    doc.text(this.safe(text).toUpperCase(), LEFT, baseline, { charSpace: 0.35 });
    if (right) {
      this.style(8, COLORS.muted);
      doc.text(this.safe(right), LEFT + WIDTH, baseline, { align: 'right' });
    }
    this.y += 5.5;
  }

  /** "Label    value" row of the title block. */
  pair(label, value) {
    const { doc } = this;
    const size = 9.5;
    const em = size * PT;
    const height = em * 1.45;
    this.style(size, COLORS.body);
    this.wrap(value, WIDTH - 34).forEach((line, index) => {
      this.ensure(height);
      const baseline = this.y + em * 1.07;
      if (index === 0) {
        this.style(size, COLORS.muted);
        doc.text(this.safe(label), LEFT, baseline);
        this.style(size, COLORS.body);
      }
      doc.text(line, LEFT + 34, baseline);
      this.y += height;
    });
  }

  cover(conversation, plan, exportedAt, exportedBy) {
    const investigation = conversation.mode === 'investigation';
    const scope = investigation ? conversation.scopeInfo : null;
    const questions = plan.items.filter((item) => item.type === 'question').length;
    const answers = plan.items.filter((item) => item.type === 'answer').length;
    const evidence = this.known.size;
    const contents = [
      count(questions, ['вопрос', 'вопроса', 'вопросов']),
      count(answers, ['ответ', 'ответа', 'ответов']),
      evidence ? count(evidence, ['доказательство', 'доказательства', 'доказательств']) : '',
    ];
    const created = when(conversation.createdAt);
    const updated = when(conversation.updatedAt);
    const rows = [
      ['Создано', created],
      ['Обновлено', updated !== created ? updated : ''],
    ];
    if (scope) {
      rows.push(
        ['Период', scope.period],
        ['Компьютеры', scope.computers ? count(scope.computers, ['компьютер', 'компьютера', 'компьютеров']) : ''],
        ['Источники', scope.sources],
      );
    }
    rows.push(
      ['Содержание', contents.filter(Boolean).join(', ')],
      ['Выгружено', [when(exportedAt), exportedBy].filter(Boolean).join(' · ')],
    );

    this.label(`AI Ассистент · ${investigation ? 'Расследование' : 'Чат'}`);
    this.gap(1);
    this.text(conversation.title || 'Без названия', { size: 18, color: COLORS.text, leading: 1.3, after: 3 });
    rows.filter(([, value]) => value).forEach(([label, value]) => this.pair(label, value));
    this.gap(3);
    this.rule();
    this.gap(6);
  }

  question({ message, index }) {
    const meta = message.meta || {};
    this.ensure(24);
    this.label(`Вопрос ${index}`, when(message.createdAt));
    this.gap(1);
    // A question of files only has no text strip.
    if (message.content) {
      this.strip(COLORS.primarySoft, 2);
      this.text(message.content, { size: 10, color: COLORS.text, fill: COLORS.primarySoft });
      this.strip(COLORS.primarySoft, 2);
    }
    const files = Array.isArray(meta.attachments) ? meta.attachments : [];
    if (files.length) {
      this.gap(1.5);
      this.text(`Приложенные файлы · ${files.length}`, { size: 8.5, color: COLORS.muted, after: 0.5 });
      files.forEach((file) => {
        const linked = !!file.evidence && this.known.has(file.evidence);
        this.text([file.name || 'Файл', fileSize(file.size)].filter(Boolean).join(' · '), {
          size: 9,
          color: linked ? COLORS.primary : COLORS.body,
          indent: 4,
          marker: { text: '•', x: LEFT + 0.8, color: COLORS.muted },
          target: linked ? file.evidence : null,
        });
      });
    }
    this.gap(5);
  }

  answer({ message, index, ids, cite }) {
    const meta = message.meta || {};
    this.ensure(24);
    this.label(`Ответ ${index}`, when(message.createdAt));
    this.gap(1.5);
    if (message.content) this.markdown(message.content, cite);
    (meta.notes || []).filter(Boolean).forEach((note) => {
      this.text(`Примечание: ${note}`, { size: 8.5, color: COLORS.muted, after: 0.8 });
    });
    const problem = problemOf(meta);
    if (problem) {
      const color = meta.status === 'failed' ? COLORS.danger : COLORS.warning;
      this.text(`Ответ прерван — ${problem}.`, { size: 9.5, color, after: 1 });
    }
    if (ids.length) {
      const total = count(ids.length, ['доказательство', 'доказательства', 'доказательств']);
      this.text(`${total} к ответу — в разделе «Доказательства»`, {
        size: 8.5,
        color: COLORS.primary,
        target: `group:${message.id}`,
        after: 1,
      });
    }
    this.gap(2.5);
    this.rule();
    this.gap(6);
  }

  markdown(source, cite) {
    const tokens = marked.lexer(String(source || ''), { gfm: true, breaks: true, headerIds: false, mangle: false });
    this.blocks(tokens, { cite, indent: 0, depth: 0 });
  }

  blocks(tokens, ctx) {
    (tokens || []).forEach((token) => this.block(token, ctx));
  }

  block(token, ctx) {
    switch (token.type) {
      case 'heading':
        this.heading(token, ctx);
        break;
      case 'paragraph':
      case 'text':
        this.paragraph(token.tokens ? inline(token.tokens, ctx.cite.hrefs) : decode(token.text), ctx);
        break;
      case 'list':
        this.list(token, ctx);
        break;
      case 'table':
        this.table(token, ctx);
        break;
      case 'code':
        this.code(token, ctx);
        break;
      case 'blockquote':
        this.blocks(token.tokens, {
          ...ctx,
          indent: ctx.indent + 5,
          bar: LEFT + ctx.indent + 1,
          color: COLORS.muted,
        });
        break;
      case 'hr':
        this.gap(1.5);
        this.rule(ctx.indent);
        this.gap(3);
        break;
      case 'html':
        this.paragraph(decode(String(token.text || '').replace(/<[^>]*>/g, '')), ctx);
        break;
      default:
        break;
    }
  }

  paragraph(raw, ctx) {
    const text = numberCitations(raw, ctx.cite.numbers).replace(/\n+$/, '');
    const { marker } = ctx;
    if (!text.trim() && !marker) return;
    ctx.marker = null;
    // Paragraphs of a list item are closer than the paragraphs of the answer.
    let after = ctx.depth ? 1.2 : 2.2;
    if (ctx.tight) after = 0.6;
    this.text(text, {
      size: ctx.size || 10,
      color: ctx.color || COLORS.body,
      indent: ctx.indent,
      cite: ctx.cite,
      marker,
      bar: ctx.bar === undefined ? null : ctx.bar,
      after,
    });
  }

  heading(token, ctx) {
    const sizes = { 1: 14, 2: 12.5, 3: 11.5 };
    const size = sizes[token.depth] || 10.5;
    this.gap(token.depth <= 2 ? 3 : 2);
    this.ensure(size * PT * 1.35 + 12); // keeps the heading with the next lines
    this.text(numberCitations(inline(token.tokens, ctx.cite.hrefs), ctx.cite.numbers), {
      size,
      color: COLORS.text,
      indent: ctx.indent,
      leading: 1.35,
      cite: ctx.cite,
      after: 1.2,
    });
  }

  list(token, ctx) {
    const start = Number(token.start) || 1;
    const width = token.ordered ? 6.5 : 4.5;
    token.items.forEach((item, position) => {
      let text = ctx.depth % 2 ? '–' : '•';
      if (token.ordered) text = `${start + position}.`;
      if (item.task) text = item.checked ? '[x]' : '[ ]';
      const marker = { text, x: LEFT + ctx.indent + 0.8, color: token.ordered ? COLORS.body : COLORS.muted };
      const inner = {
        ...ctx,
        indent: ctx.indent + (item.task ? 7 : width),
        depth: ctx.depth + 1,
        tight: !token.loose,
        marker,
      };
      this.blocks(item.tokens, inner);
      // An empty item still shows its marker.
      if (inner.marker) this.text(' ', { indent: inner.indent, marker, after: 0.6 });
    });
    if (!ctx.depth || !ctx.tight) this.gap(1.6);
  }

  table(token, ctx) {
    const cell = (entry) => this.safe(numberCitations(inline(entry.tokens, ctx.cite.hrefs), ctx.cite.numbers));
    const columnStyles = {};
    (token.align || []).forEach((align, index) => {
      if (align === 'right' || align === 'center') columnStyles[index] = { halign: align };
    });
    this.gap(0.5);
    this.grid({
      head: [token.header.map(cell)],
      body: token.rows.map((row) => row.map(cell)),
      indent: ctx.indent,
      size: 8.5,
      cite: ctx.cite,
      columnStyles,
    });
    this.gap(1);
  }

  /** A code block: a shaded box, monospaced when the code is plain ASCII (Courier has no Cyrillic). */
  code(token, ctx) {
    const { doc } = this;
    const source = String(token.text || '')
      .replace(/\r/g, '')
      .replace(/\t/g, '    ');
    const font = /^[ -~\n]*$/.test(source) ? 'courier' : FONT;
    const size = 8.5;
    const em = size * PT;
    const height = em * 1.35;
    const x = LEFT + ctx.indent;
    const width = WIDTH - ctx.indent;
    const box = (top, h) => {
      doc.setFillColor(...COLORS.code);
      doc.rect(x, top, width, h, 'F');
      doc.setFillColor(...COLORS.border);
      doc.rect(x, top, 0.8, h, 'F');
    };
    this.gap(0.5);
    this.ensure(2 + height);
    box(this.y, 2);
    this.y += 2;
    this.style(size, COLORS.body, font);
    const lines = source
      .replace(/\n+$/, '')
      .split('\n')
      .reduce((result, part) => result.concat(part ? doc.splitTextToSize(this.safe(part), width - 6) : ['']), []);
    lines.forEach((line) => {
      if (this.y + height > LIMIT) {
        this.newPage();
        this.style(size, COLORS.body, font);
      }
      box(this.y, height);
      if (line) doc.text(line, x + 3.5, this.y + em * 1.02);
      this.y += height;
    });
    this.ensure(2);
    box(this.y, 2);
    this.y += 2;
    this.style(10, COLORS.body);
    this.gap(2.5);
  }

  /** A table (autoTable): page breaks repeat the head; numbered citations in cells become links. */
  grid({ head = null, body, indent = 0, size = 8.5, cite = null, columnStyles = {}, plain = false }) {
    const { doc } = this;
    this.ensure(12);
    const options = {
      startY: this.y,
      body,
      theme: plain ? 'plain' : 'grid',
      margin: { top: TOP, bottom: PAGE_HEIGHT - LIMIT, left: LEFT + indent, right: LEFT },
      styles: {
        font: FONT,
        fontStyle: 'normal',
        fontSize: size,
        textColor: COLORS.body,
        lineColor: COLORS.border,
        lineWidth: plain ? 0 : 0.2,
        cellPadding: plain ? { top: 0.5, right: 3, bottom: 0.5, left: 0 } : Math.min(1.4, size / 6.5),
        valign: 'top',
        overflow: 'linebreak',
      },
      headStyles: {
        font: FONT,
        fontStyle: 'normal',
        fillColor: COLORS.primarySoft,
        textColor: COLORS.text,
        lineColor: COLORS.border,
        lineWidth: 0.2,
      },
      columnStyles,
      rowPageBreak: 'auto',
      showHead: head ? 'everyPage' : 'never',
    };
    if (head) options.head = head;
    if (cite) options.didDrawCell = (data) => this.cellLinks(data, cite);
    this.autoTable(doc, options);
    this.y = doc.lastAutoTable.finalY + 2.5;
    this.style(10, COLORS.body);
  }

  cellLinks({ cell, doc }, cite) {
    const lines = cell.text || [];
    if (!lines.some((line) => line.includes('['))) return;
    const em = cell.styles.fontSize / doc.internal.scaleFactor;
    const lineHeight = em * doc.getLineHeightFactor();
    const position = cell.getTextPos();
    lines.forEach((line, index) => {
      const spans = spansOf(line, cite, []).filter((span) => !span.url);
      if (!spans.length) return;
      // autoTable draws the first baseline at top + 0.85 em (its "align the top" shift).
      const baseline = position.y + em * 0.85 + index * lineHeight;
      let shift = 0;
      if (cell.styles.halign === 'right') shift = doc.getTextWidth(line);
      if (cell.styles.halign === 'center') shift = doc.getTextWidth(line) / 2;
      const x = position.x - shift;
      this.links(line, x, baseline, em, spans, cite);
    });
  }

  appendix(groups, evidence) {
    const listed = groups.filter((group) => group.ids.length);
    if (!listed.length) return;
    this.newPage();
    const start = { page: this.page, top: this.y };
    this.text('Доказательства', { size: 16, color: COLORS.text, leading: 1.3, after: 1 });
    this.text(
      'Записи, на которые ссылаются ответы. Номера совпадают с номерами ссылок в тексте ответов, ' +
        'ссылки «Открыть в журнале» открывают запись в веб-интерфейсе.',
      { size: 8.5, color: COLORS.muted, after: 2.5 },
    );
    this.rule();
    this.gap(5);
    const shown = {};
    listed.forEach((group) => {
      if (listed.length > 1) {
        this.ensure(34);
        this.targets[`group:${group.id}`] = { page: this.page, top: this.y };
        this.text(`К ответу ${group.index}`, { size: 12.5, color: COLORS.text, after: 0.3 });
        if (group.question) {
          this.text(`Вопрос: ${group.question}`, { size: 8.5, color: COLORS.muted, single: true });
        }
        this.gap(4);
      } else {
        this.targets[`group:${group.id}`] = start;
      }
      group.ids.forEach((id) => {
        const number = group.numbers[id];
        this.card(number, id, evidence[id], shown[id]);
        if (!shown[id]) shown[id] = { number, index: group.index };
      });
    });
  }

  card(number, id, item, shown) {
    this.ensure(28);
    if (shown) {
      this.cardHead(number, 'То же доказательство', '');
      this.text(`Приведено выше: [${shown.number}] к ответу ${shown.index}.`, {
        size: 9,
        color: COLORS.primary,
        indent: CARD_INDENT,
        target: id,
      });
    } else {
      this.targets[id] = { page: this.page, top: Math.max(this.y - 2, 0) };
      if (!item || !item.card) this.missing(number, item);
      else if (item.card.kind === 'finding') this.finding(number, item);
      else this.record(number, item);
    }
    this.gap(2.5);
    this.rule();
    this.gap(4.5);
  }

  /** Head of a card: the number, what it is, notes in colour and the time on the right. */
  cardHead(number, title, right, extras = []) {
    const { doc } = this;
    this.ensure(15);
    const top = this.y;
    const baseline = top + 4.6;
    const label = String(number || '•');
    this.style(8.5, COLORS.primary);
    const badge = Math.max(5.6, doc.getTextWidth(label) + 3);
    doc.setFillColor(...COLORS.primarySoft);
    doc.roundedRect(LEFT, top + 1.2, badge, 4.8, 1.6, 1.6, 'F');
    doc.text(label, LEFT + badge / 2, top + 4.65, { align: 'center' });
    let end = LEFT + WIDTH;
    if (right) {
      this.style(8.5, COLORS.muted);
      const shown = this.safe(right);
      doc.text(shown, end, baseline, { align: 'right' });
      end -= doc.getTextWidth(shown) + 4;
    }
    let x = LEFT + CARD_INDENT;
    this.style(10.5, COLORS.text);
    const name = this.fit(title, end - x);
    doc.text(name, x, baseline);
    x += doc.getTextWidth(name);
    extras
      .filter((extra) => extra && extra.text)
      .forEach((extra) => {
        if (end - x < 12) return;
        this.style(9, COLORS.muted);
        doc.text(' · ', x, baseline);
        x += doc.getTextWidth(' · ');
        this.style(9, extra.color || COLORS.muted);
        const text = this.fit(extra.text, end - x);
        doc.text(text, x, baseline);
        x += doc.getTextWidth(text);
      });
    this.y += 7.5;
  }

  /** Small grey caption inside a card. */
  caption(text, indent = CARD_INDENT) {
    this.gap(0.8);
    this.text(text, { size: 8, color: COLORS.muted, indent, after: 0.4 });
  }

  /** A row of links: {label, url} opens the web interface, {label, target} goes to a card of the appendix. */
  linkRow(entries, indent = CARD_INDENT) {
    if (!entries.length) return;
    const { doc } = this;
    const size = 8.5;
    const height = 5;
    this.ensure(height);
    let x = LEFT + indent;
    entries.forEach((entry) => {
      this.style(size, entry.url || entry.target ? COLORS.primary : COLORS.muted);
      const text = this.safe(entry.label);
      const width = doc.getTextWidth(text);
      if (x > LEFT + indent && x + width > LEFT + WIDTH) {
        this.y += height;
        this.ensure(height);
        x = LEFT + indent;
      }
      const baseline = this.y + 3.5;
      if (entry.url) doc.textWithLink(text, x, baseline, { url: entry.url });
      else doc.text(text, x, baseline);
      if (entry.target) this.pending.push({ page: this.page, x, y: this.y, w: width, h: height, target: entry.target });
      if (entry.url || entry.target) {
        doc.setDrawColor(...COLORS.primary);
        doc.setLineWidth(0.15);
        doc.line(x, baseline + 0.7, x + width, baseline + 0.7);
      }
      x += width + 6;
    });
    this.y += height;
  }

  record(number, item) {
    const { card } = item;
    const indent = CARD_INDENT;
    const small = { size: 8.5, color: COLORS.muted, indent, after: 0.5 };
    const isFile = item.category === 'file' || card.source === 'file';
    this.cardHead(number, card.sourceLabel || 'Запись', formatTime(item.time || card.time), [flagOf(card)]);
    if (item.computerName) this.text(`Компьютер: ${item.computerName}`, small);
    const parties = partiesOf(card);
    if (parties) this.text(parties, { ...small, color: COLORS.body });
    if (card.title) this.text(card.title, { size: 10, color: COLORS.text, indent, after: 0.6 });
    if (card.action) this.text(card.action, { size: 9, color: COLORS.muted, indent, after: 0.6 });
    if (card.text) {
      const label = isFile ? TEXT_LABELS[card.textKind] : '';
      if (label) this.caption(label);
      else this.gap(0.8);
      this.strip(COLORS.soft, 1.6, indent);
      this.text(card.text, { size: 9, color: COLORS.body, indent, fill: COLORS.soft, leading: 1.38 });
      this.strip(COLORS.soft, 1.6, indent);
    }
    const files = card.attachments || [];
    if (files.length) {
      this.caption('Вложения');
      files.forEach((file) => {
        this.text([file.name, fileSize(file.size)].filter(Boolean).join(' · '), {
          size: 9,
          indent: indent + 4,
          marker: { text: '•', x: LEFT + indent + 0.8, color: COLORS.muted },
          after: 0.2,
        });
      });
    }
    if (card.fields && card.fields.length) {
      this.gap(1.2);
      this.grid({
        body: card.fields.map((field) => [this.safe(field.label), this.safe(field.value)]),
        indent,
        size: 8.5,
        plain: true,
        columnStyles: { 0: { cellWidth: 42, textColor: COLORS.muted } },
      });
    }
    if (isFile && card.parent) {
      const source = this.sources[card.parentSource];
      const linked = this.known.has(card.parent);
      const text = card.parentSource === 'upload'
        ? `Из файла пользователя${linked ? ' — перейти к файлу' : ''}`
        : `Из записи${source ? ` «${source}»` : ''}${linked ? ' — перейти к записи' : ''}`;
      this.text(text, { ...small, color: linked ? COLORS.primary : COLORS.muted, target: linked ? card.parent : null });
    }
    if (card.kind === 'report') this.report(card);
    this.gap(1);
    this.linkRow(evidenceLinks(item, this.resolveUrl));
  }

  report(card) {
    const columns = card.columns || [];
    const rows = card.rows || [];
    if (!columns.length) {
      this.text('В отчёте нет строк за этот период.', { size: 8.5, color: COLORS.muted, indent: CARD_INDENT });
      return;
    }
    const value = (entry) => this.safe(entry === null || entry === undefined ? '' : entry);
    this.gap(1.2);
    this.grid({
      head: [columns.map(value)],
      body: rows.slice(0, REPORT_ROWS).map((row) => (Array.isArray(row) ? row : Object.values(row || {})).map(value)),
      indent: CARD_INDENT,
      size: columns.length > 8 ? 6.5 : 7.5,
    });
    if (rows.length > REPORT_ROWS) {
      const rest = count(rows.length - REPORT_ROWS, ['строка', 'строки', 'строк']);
      this.text(`и ещё ${rest} — полностью в отчёте`, { size: 8.5, color: COLORS.muted, indent: CARD_INDENT });
    }
  }

  finding(number, item) {
    const data = findingOf(item);
    const indent = CARD_INDENT;
    const small = { size: 8.5, color: COLORS.muted, indent, after: 0.5 };
    this.cardHead(number, 'Возможный сторонний проект', findingPeriod(data), [
      { text: data.levelLabel || 'уровень не указан', color: LEVEL_COLORS[data.level] || COLORS.muted },
      { text: count(Number(data.score) || 0, ['балл', 'балла', 'баллов']) },
    ]);
    if (data.computer) this.text(`Компьютер: ${data.computer}`, small);
    this.text(data.file || 'Файл без имени', { size: 10, color: COLORS.text, indent, after: 0.4 });
    if (data.path) this.text(data.path, small);
    const facts = [projectLabel(data), data.qualityLabel, data.activeTime ? `работа ${data.activeTime}` : ''];
    this.text(facts.filter(Boolean).join(' · '), { size: 9, color: COLORS.body, indent, after: 0.5 });

    const reasons = data.reasons || [];
    if (reasons.length) {
      this.caption('Признаки');
      reasons.forEach((reason) => {
        this.text(reason.text || '', {
          size: 9,
          indent: indent + 13,
          marker: { text: `${reason.rule || ''} +${reason.points || 0}`, x: LEFT + indent, color: COLORS.muted },
          after: 0.3,
        });
        this.linkRow((reason.refs || []).map((ref) => this.refLink(ref)), indent + 13);
      });
    }
    const transfers = data.transfers || [];
    if (transfers.length) {
      this.caption('Передачи файла');
      transfers.forEach((transfer) => {
        const recipients = (transfer.recipients || []).join(', ');
        const text = [
          clock(transfer.time),
          transfer.channel || 'канал не указан',
          recipients ? `получатели: ${recipients}` : '',
          transfer.name && transfer.name !== data.file ? `«${transfer.name}»` : '',
        ];
        const linked = !!transfer.evidence && this.known.has(transfer.evidence);
        this.text(text.filter(Boolean).join(' · '), {
          size: 9,
          color: linked ? COLORS.primary : COLORS.body,
          indent: indent + 4,
          marker: { text: '•', x: LEFT + indent + 0.8, color: COLORS.muted },
          target: linked ? transfer.evidence : null,
          after: 0.2,
        });
      });
    }
    const work = data.work || [];
    if (work.length) {
      this.caption(`Работа с файлом · ${work.length}`);
      work.forEach((entry) => {
        const duration = entry.seconds ? minutes(entry.seconds) : '';
        const text = [clock(entry.time), entry.caption || 'окно программы', duration];
        this.text(text.filter(Boolean).join(' · '), {
          size: 9,
          indent: indent + 4,
          marker: { text: '•', x: LEFT + indent + 0.8, color: COLORS.muted },
          after: 0.2,
        });
      });
    }
    this.gap(1);
    this.linkRow(evidenceLinks(item, this.resolveUrl));
  }

  /** A record behind a reason of a finding: its log page, else its card in the appendix. */
  refLink(ref) {
    let source = this.sources[ref.category] || ref.category || 'Запись';
    if (ref.category === 'activity_feed') source = 'Лента активности';
    const label = [source, clock(ref.time)].filter(Boolean).join(' · ');
    const location = recordLink({
      linkSource: ref.category,
      computerId: ref.computerId,
      recordId: ref.recordId,
      time: ref.time,
    });
    const url = location ? this.resolveUrl(location) : null;
    if (url) return { label, url };
    if (ref.evidence && this.known.has(ref.evidence)) return { label, target: ref.evidence };
    return { label };
  }

  missing(number, item) {
    this.cardHead(number, 'Доказательство недоступно', '');
    const reason =
      item && item.error ? 'Запись не удалось загрузить при выгрузке.' : 'Нет доступа к записи, или она удалена.';
    this.text(reason, { size: 9, color: COLORS.muted, indent: CARD_INDENT });
  }

  /** Internal links to the cards, now that their pages are known. */
  resolveLinks() {
    let added = 0;
    this.pending.forEach(({ page, x, y, w, h, target }) => {
      const destination = this.targets[target];
      if (!destination) return;
      this.doc.setPage(page);
      this.doc.link(x, y, w, h, { pageNumber: destination.page, top: destination.top });
      added += 1;
    });
    return added;
  }

  /** Running header and footer of every page. */
  decorate(title, stamp) {
    const { doc } = this;
    const total = doc.getNumberOfPages();
    for (let page = 1; page <= total; page++) {
      doc.setPage(page);
      this.style(7.5, COLORS.muted);
      const right = `Выгружено ${stamp}`;
      doc.text(right, LEFT + WIDTH, 10.5, { align: 'right' });
      doc.text(this.fit(`AI Ассистент · ${title}`, WIDTH - doc.getTextWidth(right) - 8), LEFT, 10.5);
      doc.setDrawColor(...COLORS.border);
      doc.setLineWidth(0.2);
      doc.line(LEFT, 12.5, LEFT + WIDTH, 12.5);
      doc.text(`стр. ${page} из ${total}`, LEFT + WIDTH, PAGE_HEIGHT - 9, { align: 'right' });
    }
  }
}

/**
 * The PDF document of a conversation.
 * lib: { jsPDF, autoTable } with the Cyrillic font already registered for new documents.
 * input: conversation, messages, numbers (message id -> { evidence id -> number of the chat }), evidence (id -> item),
 * sources ([{slug, label}]), resolveUrl (router location -> absolute address or null), exportedAt, exportedBy.
 */
export function buildConversationPdf({ jsPDF: Pdf, autoTable }, input) {
  const { conversation = {}, messages = [], numbers = {}, evidence = {}, sources = [] } = input;
  const { resolveUrl = () => null, exportedAt = new Date(), exportedBy = '' } = input;
  const doc = new Pdf({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
  if (!doc.getFontList()[FONT]) throw new Error(`The font ${FONT} is not registered`);
  doc.setFont(FONT, 'normal');
  const plan = planOf(messages, numbers);
  const known = new Set(plan.groups.reduce((result, group) => result.concat(group.ids), []));
  const labels = sources.reduce((result, source) => ({ ...result, [source.slug]: source.label }), {});
  const writer = new PdfWriter(doc, autoTable, { resolveUrl, sources: labels, known });
  const title = conversation.title || 'Без названия';

  writer.cover(conversation, plan, exportedAt, exportedBy);
  plan.items.forEach((item) => (item.type === 'question' ? writer.question(item) : writer.answer(item)));
  writer.appendix(plan.groups, evidence);
  const internalLinks = writer.resolveLinks();
  writer.decorate(title, when(exportedAt));
  return { doc, fileName: fileNameOf(title, exportedAt), pages: doc.getNumberOfPages(), internalLinks };
}

/**
 * Builds and saves the PDF in the browser.
 * cached: evidence of the store by id; fetchEvidence(ids) -> Promise of items (GET …/evidence?ids=);
 * router: the Vue Router (links of the cards are its addresses on this origin).
 */
export async function exportConversationPdf({
  conversation,
  messages,
  numbers,
  cached = {},
  fetchEvidence = null,
  router,
  origin = window.location.origin,
  sources = [],
  exportedBy = '',
}) {
  const [pdf, tables] = await Promise.all([
    import(/* webpackChunkName: "ai-pdf" */ 'jspdf'),
    import(/* webpackChunkName: "ai-pdf" */ 'jspdf-autotable'),
  ]);
  // The module registers the Cyrillic font for every new document (jsPDF's addFonts event).
  await import(/* webpackChunkName: "ai-pdf" */ '@/components/common/logs/mixins/Roboto-Regular-normal');
  const evidence = await loadEvidence(evidenceIdsOf(numbers), cached, fetchEvidence);
  const resolveUrl = (location) => {
    const resolved = router.resolve(location);
    return resolved.route && resolved.route.matched.length ? `${origin}${resolved.href}` : null;
  };
  const autoTable = [tables.default, tables.autoTable].find((entry) => typeof entry === 'function');
  const { doc, fileName } = buildConversationPdf(
    { jsPDF: pdf.jsPDF || pdf.default, autoTable },
    { conversation, messages, numbers, evidence, sources, resolveUrl, exportedBy },
  );
  doc.save(fileName);
  return fileName;
}
